import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import {
  ShieldAlert, Users, UserX, Link2Off, UserMinus, Building2, FileWarning, AlertTriangle,
  ExternalLink, RefreshCw, ChevronRight, Activity, UserPlus, Search
} from "lucide-react";

const SEV_CONFIG = {
  critical: { label: "Critico", class: "bg-red-600 text-white", dot: "bg-red-500" },
  high:     { label: "Alto",    class: "bg-red-100 text-red-700", dot: "bg-red-400" },
  medium:   { label: "Medio",   class: "bg-amber-100 text-amber-700", dot: "bg-amber-400" },
  low:      { label: "Bajo",    class: "bg-slate-100 text-slate-600", dot: "bg-slate-300" },
};

const TYPE_CONFIG = {
  emp_no_user:         { label: "Sin usuario",         icon: UserX,         color: "text-red-600" },
  user_no_emp:         { label: "Sin ficha empleado",  icon: UserMinus,     color: "text-red-500" },
  archived_active_user:{ label: "Archivado + activo",  icon: ShieldAlert,   color: "text-orange-600" },
  leave_active_user:   { label: "Baja + usuario activo", icon: ShieldAlert, color: "text-orange-500" },
  no_manager:          { label: "Sin jefe directo",    icon: Users,         color: "text-amber-600" },
  dept_no_head:        { label: "Dept. sin responsable",icon: Building2,    color: "text-amber-500" },
  broken_ref:          { label: "Referencia rota",     icon: Link2Off,      color: "text-red-700" },
  incomplete_data:     { label: "Datos incompletos",   icon: FileWarning,   color: "text-slate-500" },
  duplicate_emp_email: { label: "Empleados duplicados", icon: Link2Off,     color: "text-red-600" },
  email_mismatch:      { label: "Email no coincide",   icon: FileWarning,   color: "text-amber-600" },
  role_dept_mismatch:  { label: "Rol vs Departamento", icon: ShieldAlert,   color: "text-amber-700" },
};

export default function DataHealthPage() {
  const { axiosAuth } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterSev, setFilterSev] = useState("all");
  const [filterType, setFilterType] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosAuth().get("/data-health");
      setData(res.data);
    } catch { toast.error("Error al cargar salud de datos"); }
    finally { setLoading(false); }
  }, [axiosAuth]);

  useEffect(() => { load(); }, [load]);

  const filtered = (data?.issues || []).filter(i => {
    if (filterSev !== "all" && i.severity !== filterSev) return false;
    if (filterType !== "all" && i.type !== filterType) return false;
    return true;
  });

  const handleAction = (issue) => {
    if (issue.action === "deactivate_user" || issue.action === "open_user") {
      navigate("/admin/usuarios", { state: { highlight: issue.entity_id } });
    } else if (issue.entity === "employee" || issue.action === "open_employee" || issue.action === "create_user") {
      navigate("/admin/empleados", { state: { highlight: issue.entity_id } });
    } else if (issue.entity === "user" || issue.action === "link_employee") {
      navigate("/admin/usuarios/migracion");
    } else if (issue.action === "open_department") {
      navigate("/admin/rrhh");
    }
  };

  const kpis = data?.kpis || {};
  const bySev = data?.by_severity || {};
  const byType = data?.by_type || {};

  return (
    <div className="space-y-6" data-testid="data-health-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="heading-industrial text-2xl font-bold text-slate-900 uppercase flex items-center gap-2" data-testid="data-health-title">
            <Activity className="w-6 h-6 text-red-600" /> Salud de Datos
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">Deteccion y correccion de inconsistencias</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}
          className="rounded-sm text-xs uppercase" data-testid="btn-refresh">
          <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Actualizar
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <KpiCard label="Empleados activos" value={kpis.total_employees} testId="kpi-employees" />
        <KpiCard label="Archivados" value={kpis.total_archived} testId="kpi-archived" />
        <KpiCard label="Usuarios" value={kpis.total_users} testId="kpi-users" />
        <KpiCard label="Vinculaciones OK" value={kpis.linked_ok} color="text-green-600" testId="kpi-linked" />
        <KpiCard label="Docs. por validar" value={kpis.docs_pending_validation} color={kpis.docs_pending_validation > 0 ? "text-amber-600" : ""} testId="kpi-docs" />
        <KpiCard label="Incidencias totales" value={data?.total_issues} color={data?.total_issues > 0 ? "text-red-600" : "text-green-600"} testId="kpi-issues" />
      </div>

      {/* Severity Summary */}
      {data?.total_issues > 0 && (
        <div className="flex flex-wrap gap-2">
          {Object.entries(bySev).filter(([,c]) => c > 0).map(([sev, count]) => (
            <button key={sev} onClick={() => setFilterSev(filterSev === sev ? "all" : sev)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-sm border text-xs font-medium transition-all ${filterSev === sev ? "ring-2 ring-offset-1 ring-slate-400" : ""}`}
              data-testid={`filter-sev-${sev}`}>
              <span className={`w-2 h-2 rounded-full ${SEV_CONFIG[sev]?.dot}`} />
              {SEV_CONFIG[sev]?.label}: <strong>{count}</strong>
            </button>
          ))}
          {Object.entries(byType).filter(([,c]) => c > 0).map(([type, count]) => {
            const tc = TYPE_CONFIG[type] || {};
            return (
              <button key={type} onClick={() => setFilterType(filterType === type ? "all" : type)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-sm border text-xs font-medium transition-all ${filterType === type ? "ring-2 ring-offset-1 ring-slate-400" : ""}`}
                data-testid={`filter-type-${type}`}>
                {tc.icon && <tc.icon className={`w-3 h-3 ${tc.color}`} />}
                {tc.label || type}: <strong>{count}</strong>
              </button>
            );
          })}
          {(filterSev !== "all" || filterType !== "all") && (
            <button onClick={() => { setFilterSev("all"); setFilterType("all"); }}
              className="px-3 py-1.5 rounded-sm border border-red-200 text-xs text-red-500 font-medium hover:bg-red-50">
              Limpiar filtros
            </button>
          )}
        </div>
      )}

      {/* Issues Table */}
      <Card className="border-slate-200 rounded-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-xs uppercase tracking-wider font-semibold flex items-center justify-between">
            <span className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              Incidencias detectadas
            </span>
            <Badge variant="outline" className="text-[10px] rounded-sm">{filtered.length} resultados</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-[10px] uppercase w-[80px]">Severidad</TableHead>
                  <TableHead className="text-[10px] uppercase">Tipo</TableHead>
                  <TableHead className="text-[10px] uppercase">Entidad</TableHead>
                  <TableHead className="text-[10px] uppercase">Detalle</TableHead>
                  <TableHead className="text-[10px] uppercase text-right w-[100px]">Accion</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-10 text-slate-400 text-xs">Analizando datos...</TableCell></TableRow>
                ) : filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-10" data-testid="no-issues">
                      <div className="flex flex-col items-center gap-2">
                        <Activity className="w-8 h-8 text-green-400" />
                        <p className="text-sm font-medium text-green-600">Sin incidencias</p>
                        <p className="text-xs text-slate-400">Todos los datos estan correctos</p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((issue, idx) => {
                    const sc = SEV_CONFIG[issue.severity] || {};
                    const tc = TYPE_CONFIG[issue.type] || {};
                    const Icon = tc.icon || AlertTriangle;
                    return (
                      <TableRow key={idx} className="hover:bg-slate-50 group" data-testid={`issue-row-${idx}`}>
                        <TableCell>
                          <Badge className={`text-[8px] rounded-sm ${sc.class}`} data-testid={`issue-sev-${idx}`}>{sc.label}</Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1.5">
                            <Icon className={`w-3.5 h-3.5 ${tc.color}`} />
                            <span className="text-[11px] font-medium">{issue.label}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="text-xs font-medium text-slate-800">{issue.entity_name}</span>
                        </TableCell>
                        <TableCell>
                          <span className="text-[11px] text-slate-500">{issue.detail}</span>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" onClick={() => handleAction(issue)}
                            className="h-7 px-2 text-[10px] text-blue-600 hover:text-blue-700 opacity-0 group-hover:opacity-100 transition-opacity"
                            data-testid={`issue-action-${idx}`}>
                            {issue.action === "create_user" && <><UserPlus className="w-3 h-3 mr-1" />Crear usuario</>}
                            {issue.action === "link_employee" && <><Search className="w-3 h-3 mr-1" />Vincular</>}
                            {issue.action === "open_employee" && <><ExternalLink className="w-3 h-3 mr-1" />Abrir ficha</>}
                            {issue.action === "open_department" && <><ChevronRight className="w-3 h-3 mr-1" />Revisar</>}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function KpiCard({ label, value, color = "", testId }) {
  return (
    <Card className="border-slate-200 rounded-sm">
      <CardContent className="p-3 text-center">
        <p className={`text-2xl font-bold tabular-nums ${color || "text-slate-800"}`} data-testid={testId}>{value ?? "-"}</p>
        <p className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold mt-0.5">{label}</p>
      </CardContent>
    </Card>
  );
}
